/**
 * Modèle de données du certificat de scolarité.
 *
 * Principe : l'école ne remplit pas des champs figés (République, Devise,
 * Ministère…), elle écrit librement son en-tête ligne par ligne. Les trois
 * modèles ne définissent QUE l'habillage visuel ; le contenu reste identique
 * quel que soit le modèle choisi.
 */

/* -------------------------------------------------------------------------
 * Palettes
 * Chaque palette est appliquée sur la feuille sous forme de variables CSS.
 * Les couples encre / papier sont volontairement très contrastés pour rester
 * lisibles aussi bien à l'écran qu'à l'impression.
 * ---------------------------------------------------------------------- */
export const CERTIFICATE_PALETTES = [
  { id: "encre", name_fr: "Encre noire", name_en: "Black ink", paper: "#ffffff", ink: "#14161a", soft: "#5c626d", accent: "#1f2937" },
  { id: "officiel", name_fr: "Bleu officiel", name_en: "Official blue", paper: "#ffffff", ink: "#111d31", soft: "#55637c", accent: "#1d4e8f" },
  { id: "vert", name_fr: "Vert académique", name_en: "Academic green", paper: "#fdfefc", ink: "#12211a", soft: "#54685c", accent: "#1f6a45" },
  { id: "bordeaux", name_fr: "Bordeaux", name_en: "Burgundy", paper: "#fffcfb", ink: "#1f1315", soft: "#6f545a", accent: "#8b2333" },
  { id: "ivoire", name_fr: "Ivoire & or", name_en: "Ivory & gold", paper: "#fffdf5", ink: "#1b1a14", soft: "#6a6350", accent: "#8a6a1c" },
];

/** Les trois modèles proposés : uniquement de l'habillage visuel. */
export const CERTIFICATE_TEMPLATES = ["officiel", "sobre", "bandeau"];

export function paletteOf(id) {
  return CERTIFICATE_PALETTES.find((p) => p.id === id) || CERTIFICATE_PALETTES[0];
}

/**
 * En-tête bilingue camerounais fourni comme point de départ.
 * Chaque ligne est éditable, supprimable ; la colonne droite peut être vidée
 * pour obtenir un en-tête unique centré (école privée, hors Cameroun…).
 */
/**
 * Nombre maximum de lignes dans l'en-tête. Au-delà, le bloc prend trop de
 * hauteur sur la page et déséquilibre le certificat.
 */
export const MAX_HEADER_ROWS = 5;

/**
 * Les trois premières lignes (République, devise, ministère) constituent
 * l'ossature officielle du document : elles sont modifiables mais pas
 * supprimables.
 */
export const LOCKED_HEADER_ROWS = 3;

/**
 * En-tête bilingue par défaut.
 *
 * Chaque entrée est UNE ligne du document, avec sa version française et sa
 * version anglaise. Les deux sont indissociables : c'est ce qui garantit
 * qu'elles restent alignées à l'impression, même si l'une des deux est
 * assez longue pour passer sur plusieurs lignes.
 */
export function defaultHeaderRows() {
  return [
    { id: "row-republic", fr: "République du Cameroun", en: "Republic of Cameroon" },
    // La devise est traditionnellement composée en italique sur les documents
    // officiels camerounais : le style est donc porté par la ligne elle-même.
    { id: "row-motto", fr: "Paix – Travail – Patrie", en: "Peace – Work – Fatherland", italic: true },
    {
      id: "row-ministry",
      fr: "Ministère des Enseignements Secondaires",
      en: "Ministry of Secondary Education",
    },
  ];
}

/** Identifiant unique pour une nouvelle ligne d'en-tête. */
export function newHeaderRowId() {
  return `row-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;
}

/**
 * Vérifie qu'aucune ligne n'est à moitié remplie.
 * Renvoie la liste des numéros de lignes fautives (1-based) et, pour chacune,
 * la colonne manquante — de quoi afficher un message précis à l'utilisateur.
 */
export function validateHeaderRows(rows) {
  const errors = [];
  (rows || []).forEach((row, index) => {
    const fr = String(row.fr || "").trim();
    const en = String(row.en || "").trim();
    if (fr && en) return;
    if (!fr && !en) errors.push({ index: index + 1, missing: "both" });
    else errors.push({ index: index + 1, missing: fr ? "en" : "fr" });
  });
  return errors;
}

/**
 * Langue courante de l'application.
 *
 * Ce fichier n'est pas un composant : il n'a pas accès à `this.$i18n`. La
 * langue est relue depuis le même emplacement que celui utilisé par main.js
 * au démarrage, ce qui garantit la cohérence sans dépendance circulaire.
 */
export function currentLocale() {
  try {
    const saved = localStorage.getItem("locale");
    return saved === "en" ? "en" : "fr";
  } catch (error) {
    return "fr";
  }
}

/**
 * Corps du certificat par défaut, dans la langue de l'interface.
 *
 * IMPORTANT : seul le texte de liaison est traduit. Les jetons {{...}} sont
 * des identifiants techniques et restent identiques dans les deux langues —
 * un modèle enregistré en français continue donc de fonctionner si l'école
 * bascule l'interface en anglais.
 */
export function defaultBody(locale = currentLocale()) {
  if (locale === "en") {
    return [
      "I, the undersigned, **{{signatory}}**, Head of **{{school}}**, hereby certify that the student **{{student_name}}**, {{born}} on **{{date_of_birth}}** at **{{place_of_birth}}**,",
      "is duly enrolled in our school under registration number **{{registration_no}}**, in class **{{class}}**, for the **{{school_year}}** academic year.",
      "This certificate is issued to serve and avail as of right.",
    ].join("\n\n");
  }
  return [
    "Je soussigné(e), **{{signataire}}**, Chef de l'établissement **{{ecole}}**, certifie que l'élève **{{nom_eleve}}**, {{ne_e}} le **{{date_naissance}}** à **{{lieu_naissance}}**,",
    "est régulièrement inscrit(e) dans notre établissement sous le matricule **{{matricule}}**, en classe de **{{classe}}**, au titre de l'année scolaire **{{annee}}**.",
    "En foi de quoi, le présent certificat lui est délivré pour servir et valoir ce que de droit.",
  ].join("\n\n");
}

/** Titre du document par défaut. */
export function defaultTitle(locale = currentLocale()) {
  return locale === "en" ? "School Attendance Certificate" : "Certificat de Scolarité";
}

/** Fonction du signataire par défaut. */
export function defaultSignatoryRole(locale = currentLocale()) {
  return locale === "en" ? "The Principal" : "Le Directeur / La Directrice";
}

export function defaultCertificate(locale = currentLocale()) {
  const year = new Date().getFullYear();
  return {
    /* --- Habillage --- */
    template: "officiel",
    palette: "encre",

    /* --- En-tête libre : une ligne de texte = une ligne affichée --- */
    /* En-tête : une entrée = une ligne, avec ses deux versions. */
    header_rows: defaultHeaderRows(),
    logo_url: "",

    /* --- Contenu --- */
    title: defaultTitle(locale),
    body: defaultBody(locale),

    /* --- Pied de page --- */
    place: "",
    date: "",
    signatory_name: "",
    signatory_role: defaultSignatoryRole(locale),
    show_seal: true,

    /* --- Conservé pour la numérotation des documents émis --- */
    reference_prefix: "CS",
    school_year: `${year}-${year + 1}`,
  };
}

/**
 * Convertit un certificat enregistré dans une ancienne structure
 * (champs figés republic_fr / motto_fr / ministry_fr…, ou liste header_lines)
 * vers le nouveau modèle, afin de ne pas perdre ce qu'une école a déjà saisi.
 */
export function migrateCertificate(saved) {
  if (!saved || typeof saved !== "object") return defaultCertificate();
  const base = defaultCertificate();

  // Déjà au nouveau format.
  if (Array.isArray(saved.header_rows)) {
    return {
      ...base,
      ...saved,
      // L'identifiant "row-school" désigne la ligne « nom de l'établissement »
      // que l'application injectait automatiquement dans l'en-tête. Cet ajout
      // a été retiré : la ligne est donc supprimée des configurations qui la
      // contiennent encore. Seules ces lignes générées portent cet id — celles
      // saisies par l'administrateur ont un identifiant unique et sont
      // conservées telles quelles.
      header_rows: saved.header_rows.filter((row) => row.id !== "row-school"),
    };
  }

  const left = [];
  const right = [];

  if (typeof saved.header_left === "string") {
    // Format précédent : deux textes libres, une ligne de texte = une ligne.
    const split = (text) =>
      String(text || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    left.push(...split(saved.header_left));
    right.push(...split(saved.header_right));
  } else if (Array.isArray(saved.header_lines)) {
    // Format intermédiaire : liste de lignes marquées gauche / droite.
    saved.header_lines.forEach((line) => {
      if (!line || !line.text) return;
      (line.side === "right" ? right : left).push(line.text);
    });
  } else {
    // Format d'origine : huit champs figés.
    [saved.republic_fr, saved.motto_fr, saved.ministry_fr, saved.delegation_fr].forEach((t) => {
      if (t && String(t).trim()) left.push(t);
    });
    [saved.republic_en, saved.motto_en, saved.ministry_en, saved.delegation_en].forEach((t) => {
      if (t && String(t).trim()) right.push(t);
    });
  }

  // Les deux colonnes sont appariées rang par rang. Une colonne plus courte
  // que l'autre laisse des champs vides : la validation les signalera à
  // l'utilisateur plutôt que d'inventer une traduction.
  const height = Math.max(left.length, right.length);
  const rows = [];
  for (let i = 0; i < height; i += 1) {
    rows.push({
      id: `row-mig-${i}`,
      fr: left[i] || "",
      en: right[i] || "",
      // La devise est reconnue par sa position habituelle (2e ligne).
      italic: i === 1,
    });
  }

  return {
    ...base,
    ...saved,
    header_rows: rows.length ? rows : defaultHeaderRows(),
    logo_url: saved.logo_url || saved.school_logo || "",
    title: saved.title || saved.header_title || base.title,
    body: saved.body || saved.body_text || base.body,
    place: saved.place || saved.school_city || "",
    signatory_name: saved.signatory_name || "",
    signatory_role: saved.signatory_role || base.signatory_role,
    show_seal: saved.show_seal !== undefined ? saved.show_seal : saved.show_stamp !== false,
  };
}

/**
 * Découpe un texte contenant des passages **en gras** en segments.
 * Renvoie [{ text, bold }] — utilisé pour le rendu du corps du certificat.
 */
export function splitBold(text) {
  return String(text || "")
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((part) => part.length > 0)
    .map((part) =>
      part.startsWith("**") && part.endsWith("**")
        ? { text: part.slice(2, -2), bold: true }
        : { text: part, bold: false }
    );
}

/** Découpe le corps en paragraphes (une ligne vide = nouveau paragraphe). */
export function splitParagraphs(text) {
  return String(text || "")
    .split(/\n\s*\n/)
    .filter((part) => part.trim().length > 0);
}

/** Découpe un en-tête en lignes non vides. */
export function splitLines(text) {
  return String(text || "")
    .split("\n")
    .filter((line) => line.trim().length > 0);
}

/* -------------------------------------------------------------------------
 * Variables dynamiques
 *
 * IMPORTANT : chaque variable ci-dessous est branchée sur un champ RÉEL
 * renvoyé par l'API. Aucune donnée n'est inventée — si un champ est absent
 * pour un élève, la variable est remplacée par un tiret de remplissage afin
 * que le document reste lisible et qu'on voie immédiatement ce qui manque.
 *
 *   élève     : /api/student/:id et /api/get_classroom_details
 *               -> registration_number, last_name, first_name, gender, dob, pob
 *   classe    : res.data.classroom -> grade_code, speciality_code
 *   école     : schoolProfile      -> name, town, owner…
 * ---------------------------------------------------------------------- */

/**
 * Variables du certificat.
 *
 * Chaque variable a un jeton français et un jeton anglais. Le résolveur
 * accepte LES DEUX : un modèle enregistré avec {{nom_eleve}} continue donc de
 * fonctionner si l'école bascule l'interface en anglais, et inversement. Sans
 * cette équivalence, traduire les jetons casserait toutes les configurations
 * déjà enregistrées.
 */
export const CERTIFICATE_VARIABLES = [
  { key: "nom_eleve", en: "student_name", source: "first_name + last_name" },
  { key: "nom", en: "last_name", source: "last_name" },
  { key: "prenom", en: "first_name", source: "first_name" },
  { key: "matricule", en: "registration_no", source: "registration_number" },
  { key: "date_naissance", en: "date_of_birth", source: "dob" },
  { key: "lieu_naissance", en: "place_of_birth", source: "pob" },
  { key: "classe", en: "class", source: "classroom.grade_code" },
  { key: "annee", en: "school_year", source: "certificate.school_year" },
  { key: "ecole", en: "school", source: "schoolProfile.name" },
  { key: "signataire", en: "signatory", source: "certificate.signatory_name" },
  { key: "lieu", en: "place", source: "certificate.place" },
  { key: "date_jour", en: "today_date", source: "date d'émission" },
];

/**
 * Traduit les jetons {{...}} d'un texte vers la langue demandée.
 *
 * Seuls les NOMS de jetons changent : {{signataire}} devient {{signatory}} en
 * anglais, et l'inverse en français. Le texte autour n'est pas touché — une
 * école ayant rédigé son propre paragraphe le conserve mot pour mot, seuls
 * ses jetons suivent la langue de l'interface.
 *
 * Sans risque pour l'existant : le résolveur accepte les deux jeux de noms
 * (voir buildVariableMap), un modèle non converti reste donc fonctionnel.
 */
export function translateTokens(text, locale) {
  const map = {};
  CERTIFICATE_VARIABLES.forEach((variable) => {
    if (!variable.en) return;
    const target = locale === "en" ? variable.en : variable.key;
    map[variable.key.toLowerCase()] = target;
    map[variable.en.toLowerCase()] = target;
  });

  return String(text || "").replace(
    /\{\{\s*([a-z_0-9]+)\s*\}\}/gi,
    (match, key) => {
      const target = map[key.toLowerCase()];
      // Jeton inconnu : laissé tel quel, pour que l'erreur reste visible.
      return target ? `{{${target}}}` : match;
    }
  );
}

/** Jeton d'une variable dans la langue demandée. */
export function variableToken(variable, locale) {
  if (locale === "en" && variable.en) return variable.en;
  return variable.key;
}

/** Marqueur affiché quand une donnée réelle est absente. */
const MISSING = "……………………";

function clean(value) {
  if (value === undefined || value === null) return "";
  const text = String(value).trim();
  return text && text.toLowerCase() !== "null" && text.toLowerCase() !== "undefined" ? text : "";
}

/** Formate une date ISO (ou déjà formatée) selon la locale demandée. */
export function formatCertificateDate(value, locale = "fr") {
  const raw = clean(value);
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw; // déjà au format texte : on respecte
  return parsed.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** « né » / « née » selon le sexe réellement enregistré. */
function bornLabel(gender, locale) {
  const g = clean(gender).toUpperCase();
  if (locale === "fr") {
    if (g.startsWith("F")) return "née";
    if (g.startsWith("M")) return "né";
    return "né(e)";
  }
  return "born";
}

/**
 * Forme neutre de « né(e) », sans élève.
 *
 * `{{ne_e}}` n'est pas une variable que l'école renseigne : c'est un accord
 * grammatical, déduit du sexe de l'élève à l'émission. L'afficher brut dans
 * l'aperçu le faisait passer pour un champ à remplir.
 */
export function neutralBornLabel(locale) {
  return locale === "en" ? "born" : "né(e)";
}

/** Intitulé de classe : grade + spécialité si elle est réellement renseignée. */
function classLabel(classroom) {
  // `grade_code` est un CODE technique (« CIN »), pas un intitulé lisible.
  // Le nom du niveau (« 5ème ») se trouve dans l'objet `grade` imbriqué :
  // on le préfère, avec repli sur le code s'il n'est pas fourni.
  const gradeName = clean(classroom && classroom.grade && classroom.grade.name);
  const grade = gradeName || clean(classroom && classroom.grade_code);
  const speciality = clean(classroom && classroom.speciality_code);
  // L'API renvoie « Aucune Specialité » / « No Speciality » (accentuation variable)
  // lorsqu'il n'y a pas de spécialité : on normalise avant de comparer.
  const normalized = speciality
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  const isNoSpeciality = !speciality || /aucune\s*specialit|no\s*specialit/.test(normalized);
  return [grade, isNoSpeciality ? "" : speciality].filter(Boolean).join(" ");
}

/**
 * Construit la table de substitution pour un élève donné.
 * Retourne aussi la liste des variables non résolues, ce qui permet à l'écran
 * d'avertir l'utilisateur avant impression plutôt que de sortir un document
 * troué sans le signaler.
 */
export function buildVariableMap({ student, classroom, school, certificate, locale = "fr" }) {
  const s = student || {};
  const values = {
    nom_eleve: [clean(s.first_name), clean(s.last_name)].filter(Boolean).join(" "),
    nom: clean(s.last_name),
    prenom: clean(s.first_name),
    matricule: clean(s.registration_number),
    ne_e: bornLabel(s.gender, locale),
    date_naissance: formatCertificateDate(s.dob, locale),
    lieu_naissance: clean(s.pob),
    classe: classLabel(classroom),
    annee: clean(certificate && certificate.school_year),
    ecole: clean(school && school.name),
    signataire: clean(certificate && certificate.signatory_name),
    lieu: clean(certificate && certificate.place) || clean(school && school.town),
    date_jour: formatCertificateDate(
      (certificate && clean(certificate.date)) || new Date().toISOString(),
      locale
    ),
  };

  // Chaque valeur est aussi exposée sous son jeton anglais : le résolveur
  // accepte donc indifféremment {{nom_eleve}} et {{student_name}}, ce qui
  // permet de traduire les jetons sans casser les modèles déjà enregistrés.
  CERTIFICATE_VARIABLES.forEach((variable) => {
    if (variable.en && values[variable.key] !== undefined) {
      values[variable.en] = values[variable.key];
    }
  });

  // Les doublons de langue ne sont comptés qu'une fois comme manquants.
  const missing = CERTIFICATE_VARIABLES.map((v) => v.key).filter(
    (key) => key in values && !values[key]
  );
  return { values, missing };
}

/**
 * Remplace les {{variables}} d'un texte par les valeurs réelles de l'élève.
 * Une variable inconnue est laissée telle quelle (erreur de saisie visible),
 * une variable connue mais vide devient un espace à compléter à la main.
 */
export function applyVariables(text, values) {
  return String(text || "").replace(/\{\{\s*([a-z_0-9]+)\s*\}\}/gi, (match, key) => {
    const name = key.toLowerCase();
    if (!(name in values)) return match;
    return values[name] || MISSING;
  });
}

/**
 * Renvoie une copie du certificat dont tous les champs textuels sont résolus
 * pour l'élève passé en paramètre : c'est cet objet qu'on donne au composant
 * <certificate-sheet> au moment de l'impression.
 */
export function resolveCertificate({ certificate, student, classroom, school, locale = "fr" }) {
  const { values, missing } = buildVariableMap({
    student,
    classroom,
    school,
    certificate,
    locale,
  });

  return {
    certificate: {
      ...certificate,
      header_rows: (certificate.header_rows || []).map((row) => ({
        ...row,
        fr: applyVariables(row.fr, values),
        en: applyVariables(row.en, values),
      })),
      title: applyVariables(certificate.title, values),
      body: applyVariables(certificate.body, values),
      signatory_name: applyVariables(certificate.signatory_name, values),
      signatory_role: applyVariables(certificate.signatory_role, values),
      place: values.lieu,
      date: values.date_jour,
    },
    missing,
  };
}
